import * as React from "react"
import styled from "styled-components"
import { colors, scale } from "../../styles"
import { Flex } from "../Flex"
import { Text } from "../Text"

export interface RadioProps {
	onChange?: React.ChangeEventHandler<HTMLInputElement>
	disabled?: boolean
	id?: string
	name?: string
	value?: string
	checked?: boolean
	label?: React.ReactNode
	inputRef?: React.Ref<HTMLInputElement>
	helperText?: string
	error?: boolean
	defaultChecked?: boolean
}

interface LabelStyledProps {
	disabled?: boolean
}

const LabelStyled = styled.label<LabelStyledProps>`
	position: relative;
	display: inline-flex;
	cursor: pointer;
	font-size: ${scale.px(2)};
	line-height: ${scale.px(3)};
	color: ${colors.navy};
	user-select: none;
`

const LabelTextStyled = styled.span`
	padding-left: ${scale.px(1)};
	display: flex;
	align-items: center;
`

const CustomInputInner = styled.div<{ error: RadioProps["error"] }>`
	position: relative;
	display: flex;
	align-items: center;

	&:before,
	&:after {
		content: "";
		display: inline-block;
		border-radius: 50%;
		flex: 1 0 auto;
		align-self: flex-start;
	}

	// Outer Radio circle
	&:before {
		width: ${scale.px(3)};
		height: ${scale.px(3)};
		border: 1px solid ${({ error }) => (error ? colors.alert : colors.blue33)};
		background-color: ${colors.white};
	}

	// Inner Radio circle
	&:after {
		position: absolute;
		top: 7px;
		left: 7px;
		background-color: ${colors.navy};
		width: ${scale.px(1.25)};
		height: ${scale.px(1.25)};
		transform: scale(0);
		transition: transform 0.1s ease;
	}
`

const CustomInputStyled = styled.div`
	position: relative;

	&:hover {
		${CustomInputInner} {
			:before {
				border-color: ${colors.hoverBlue};
			}
		}
	}
`

const InputStyled = styled.input.attrs({ type: "radio" })`
	border: 0;
	outline: 0;
	height: 1px;
	margin: -1px;
	overflow: hidden;
	padding: 0;
	position: absolute;
	white-space: nowrap;
	width: 1px;

	&:checked + .radio {
		${CustomInputInner}:after {
			transform: scale(1);
		}
	}

	&:focus + .radio {
		${CustomInputInner}:before {
			border-color: ${colors.hoverBlue};
		}
	}

	&:disabled + .radio {
		opacity: 0.5;
	}
`

const Radio: React.FC<RadioProps> = (props) => {
	const { helperText, error, label, id, disabled, inputRef, ...rest } = props

	return (
		<Flex flexDirection="column">
			<LabelStyled disabled={disabled} htmlFor={id}>
				<InputStyled ref={inputRef} id={id} disabled={disabled} {...rest} />
				<CustomInputStyled className="radio">
					<Flex flexDirection={"column"}>
						<CustomInputInner error={error}>
							<LabelTextStyled>{label}</LabelTextStyled>
						</CustomInputInner>
					</Flex>
				</CustomInputStyled>
			</LabelStyled>
			{helperText && (
				<Flex pl={4} w={1} minh={helperText ? "none" : 3}>
					<Text
						as="span"
						textColor={error ? "alert" : "navy"}
						fontSize={1.75}
						lineHeight={2.5}
						mt={0.5}
						mb={0}
					>
						{helperText}
					</Text>
				</Flex>
			)}
		</Flex>
	)
}

export { Radio }
